import { formatDateLocal } from "../../utils/dateUtils";

/** Matches Edit Inventory "Action" dropdown and Advanced Site Search filter. */
export const INVENTORY_ACTION_OPTIONS = [
  "Disconnect",
  "Follow Up",
  "In Service",
  "Other",
  "Pending Activation",
  "Pending Disconnect",
  "Renew",
  "Replace",
  "Revenue Replacement",
];

/**
 * Formats a field name for display by adding spaces before capital letters
 * and capitalizing each word. Also handles underscores by replacing them with spaces.
 * @param {string} fieldName - The field name to format
 * @returns {string} Formatted field label
 */
export const getFieldLabel = (fieldName) => {
  return fieldName
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim()
    .split(" ") // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(" "); // Join back with spaces
};

/**
 * Checks if a field name represents a date field.
 * @param {string} fieldName - The field name to check
 * @returns {boolean} True if the field is a date field
 */
export const isDateField = (fieldName) => {
  const fieldLower = fieldName.toLowerCase();
  return (
    fieldLower.includes("date") ||
    fieldLower.includes("expir") ||
    fieldLower.includes("end") ||
    fieldLower.includes("follow_up")
  );
};

/**
 * Formats a date value for HTML5 date input (YYYY-MM-DD format).
 * Handles various date formats including:
 * - Date-only strings from backend (e.g., "2025-02-10")
 * - ISO strings with time (e.g., "2024-12-31T00:00:00.000Z")
 * - Date objects
 * - Timestamps
 *
 * CRITICAL: For date-only strings from backend (like "2025-02-10"),
 * we convert to local timezone by appending "T00:00:00" before creating Date object.
 * This treats the date as local midnight instead of UTC midnight, preventing timezone shifts.
 *
 * @param {string|Date|number} dateValue - The date value to format
 * @returns {string} Formatted date string in YYYY-MM-DD format or empty string if invalid
 */
export const formatDateForInput = (dateValue) => {
  // Handle null, undefined, or empty string
  if (!dateValue && dateValue !== 0) return "";

  try {
    let date;

    // Handle strings (most common case from backend)
    if (typeof dateValue === "string") {
      const trimmed = dateValue.trim();

      // Extract YYYY-MM-DD pattern from the string (handles ISO strings, date-only, etc.)
      const datePattern = /(\d{4}-\d{2}-\d{2})/;
      const dateMatch = trimmed.match(datePattern);

      if (dateMatch) {
        const datePart = dateMatch[1]; // Extract YYYY-MM-DD part

        // Validate the date part
        const parts = datePart.split("-");
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);

        // Basic validation: month 1-12, day 1-31, reasonable year
        if (
          month >= 1 &&
          month <= 12 &&
          day >= 1 &&
          day <= 31 &&
          year >= 1900 &&
          year <= 2100
        ) {
          // CRITICAL FIX: Convert date-only string to local timezone
          // Append "T00:00:00" to treat as local midnight, not UTC midnight
          // This prevents timezone shifting and off-by-one errors
          date = new Date(datePart + "T00:00:00");
        } else {
          return "";
        }
      } else {
        // No date pattern found, try normal Date parsing as fallback
        date = new Date(trimmed);
      }
    }
    // Handle Date objects
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    // Handle other types (numbers, etc.) - convert to string first
    else {
      const stringValue = String(dateValue);
      const datePattern = /(\d{4}-\d{2}-\d{2})/;
      const dateMatch = stringValue.match(datePattern);

      if (dateMatch) {
        const datePart = dateMatch[1];
        // Use the reliable pattern: convert to local timezone
        date = new Date(datePart + "T00:00:00");
      } else {
        date = new Date(dateValue);
      }
    }

    // Validate the date is valid
    if (!date || isNaN(date.getTime())) {
      return "";
    }

    // Extract date parts using local methods (getFullYear, getMonth, getDate)
    // This ensures we get the local date, not UTC date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    return "";
  }
};

/**
 * Checks if a field name should be rendered as a textarea.
 * @param {string} fieldName - The field name to check
 * @param {Object|null} fieldMappingLookup - Optional mapping from input column names to database column names (e.g., {"Term": "contract_term"})
 * @returns {boolean} True if the field should be a textarea
 */
export const isTextareaField = (fieldName, fieldMappingLookup = null) => {
  const fieldLower = fieldName.toLowerCase();
  // Exclude contract_term field (handles all variations: "contract_term", "Contract Term", "Contract Term (months)", "Term", etc.)
  // Check if field contains both "contract" and "term" to catch all variations
  const isContractTerm =
    fieldLower.includes("contract") && fieldLower.includes("term");

  // Also check if field maps to contract_term in fieldMappingLookup (handles "Term" -> "contract_term" case)
  let mapsToContractTerm = false;
  if (fieldMappingLookup && typeof fieldMappingLookup === "object") {
    const mappedDbColumn = fieldMappingLookup[fieldName];
    if (mappedDbColumn && mappedDbColumn.toLowerCase() === "contract_term") {
      mapsToContractTerm = true;
    }
  }

  // Exclude standalone "term" field (exactly "term" without "contract" or "end") as it typically maps to contract_term
  const isStandaloneTerm = fieldLower === "term";

  // Exclude "notes" field - it should be a text input, not a textarea
  if (fieldLower === "notes") {
    return false;
  }

  return (
    fieldLower === "address" ||
    (fieldLower.includes("address") && !fieldLower.includes("billing")) ||
    fieldLower.includes("description") ||
    (fieldLower.includes("term") &&
      !fieldLower.includes("end") &&
      !isContractTerm &&
      !mapsToContractTerm &&
      !isStandaloneTerm)
  );
};

/**
 * Checks if a field should be read-only (display only).
 * In import mode, provider and category are read-only. In inventory mode, provider and category are read-only.
 * @param {string} fieldName - The field name to check
 * @param {boolean} isImportMode - Whether we're in import mode
 * @returns {boolean} True if the field should be read-only
 */
export const isReadOnlyField = (fieldName, isImportMode) => {
  const fieldLower = fieldName.toLowerCase().trim();

  // Provider is read-only in both import and inventory modes
  if (fieldLower === "provider" && !fieldLower.includes("mile")) {
    return true;
  }

  // Category is read-only in both import and inventory modes
  if (fieldLower === "category") {
    return true;
  }

  return false;
};

/**
 * Checks if a field should be rendered as a dropdown.
 * Provider and last_mile_provider are not dropdowns.
 * @param {string} fieldName - The field name to check
 * @returns {boolean} True if the field should be a dropdown
 */
export const isDropdownField = (fieldName) => {
  const fieldLower = fieldName.toLowerCase().trim();
  // Provider and last_mile_provider should NOT be dropdowns
  if (
    fieldLower === "provider" ||
    fieldLower === "last_mile_provider" ||
    fieldLower === "last mile provider" ||
    (fieldLower.includes("provider") && fieldLower.includes("mile"))
  ) {
    return false;
  }
  // Check exact matches for other dropdown fields
  if (
    fieldLower === "category" ||
    fieldLower === "service_type" ||
    fieldLower === "servicetype" ||
    fieldLower === "service type" ||
    fieldLower === "status" ||
    fieldLower === "action"
  ) {
    return true;
  }
  // Also check if field name contains these keywords (for mapped fields)
  return (
    (fieldLower.includes("category") && fieldLower.length < 15) ||
    (fieldLower.includes("service") && fieldLower.includes("type")) ||
    fieldLower === "status"
  );
};

/**
 * Gets the dropdown options for a specific field.
 * @param {string} fieldName - The field name to get options for
 * @param {Object} dropdownValues - Optional object with providers, categories, statuses, serviceTypes arrays
 * @returns {Array<string>} Array of option values
 */
export const getDropdownOptions = (
  fieldName,
  dropdownValues = {}
) => {
  const {
    providers = [],
    categories = [],
    statuses = [],
    serviceTypes = [],
  } = dropdownValues;

  // Deduplicate function for case-insensitive deduplication
  const deduplicateArray = (array) => {
    if (!array || array.length === 0) return [];
    const seen = new Map();
    array.forEach((item) => {
      const lowerItem = String(item).toLowerCase();
      // Only add if not seen before, or if current one starts with uppercase (prefer capitalized)
      if (!seen.has(lowerItem) || 
          (item && item[0] === item[0].toUpperCase())) {
        seen.set(lowerItem, item);
      }
    });
    return Array.from(seen.values());
  };
  
  // Use only dynamic values (no fallback)
  const deduplicatedProviders = deduplicateArray(providers);
  const deduplicatedCategories = deduplicateArray(categories);
  const deduplicatedStatuses = deduplicateArray(statuses);
  const deduplicatedServiceTypes = deduplicateArray(serviceTypes);

  const fieldLower = fieldName.toLowerCase().trim();
  // Provider and last_mile_provider are not dropdowns
  if (
    fieldLower === "provider" ||
    (fieldLower.includes("provider") && !fieldLower.includes("mile"))
  ) {
    return deduplicatedProviders;
  } else if (fieldLower === "category" || fieldLower.includes("category")) {
    return deduplicatedCategories;
  } else if (
    fieldLower === "service_type" ||
    fieldLower === "servicetype" ||
    fieldLower === "service type" ||
    (fieldLower.includes("service") && fieldLower.includes("type"))
  ) {
    return deduplicatedServiceTypes;
  } else if (fieldLower === "status") {
    return deduplicatedStatuses;
  } else if (fieldLower === "action") {
    return [...INVENTORY_ACTION_OPTIONS];
  }
  return [];
};

/**
 * Gets the minimum date for follow_up_date validation (today's date).
 * @returns {string} Minimum date in YYYY-MM-DD format
 */
// export const getMinDate = () => {
//   const today = new Date();
//   return formatDateLocal(today);
// };
export const getMinDate = () => {
  const now = new Date();

  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  const day = String(now.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Gets user-friendly labels for standardized address fields.
 * @param {string} fieldName - The field name to get label for
 * @returns {string} User-friendly label for the field
 */
export const getAddressFieldLabel = (fieldName) => {
  const fieldLower = fieldName.toLowerCase();
  const labelMap = {
    std_street_address: "Street Address",
    std_city: "City",
    std_state: "State",
    std_zip: "ZIP",
    std_country: "Country",
  };
  return labelMap[fieldLower] || getFieldLabel(fieldName);
};

/**
 * Gets ordered fields for provider/category based on database mapping.
 * Prioritizes fields that exist in the mapping, then falls back to default sequence.
 * @param {Object} fieldMappingLookup - Mapping from input_column_name to inventory_column_name
 * @param {Object} formData - Current form data to check field existence
 * @param {string} provider - Current provider
 * @param {string} category - Current category
 * @param {Array} INVENTORY_FIELD_SEQUENCE - Default field sequence configuration
 * @returns {Array} Array of field configurations with priority information
 */
export const getOrderedFieldsForProviderCategory = (
  fieldMappingLookup,
  formData,
  provider,
  category,
  INVENTORY_FIELD_SEQUENCE
) => {
  // Extract mapped inventory column names from fieldMappingLookup
  const mappedFields = new Set();
  if (fieldMappingLookup && typeof fieldMappingLookup === "object") {
    Object.values(fieldMappingLookup).forEach((inventoryColumn) => {
      if (inventoryColumn && typeof inventoryColumn === "string") {
        // Normalize to lowercase for comparison
        mappedFields.add(inventoryColumn.toLowerCase());
      }
    });
  }

  // Helper function to check if a field is mapped (case-insensitive)
  const isFieldMapped = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") return false;
    const fieldLower = fieldName.toLowerCase();
    return mappedFields.has(fieldLower);
  };

  // Helper function to find actual field name in formData (case-insensitive)
  const findActualFieldName = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") return null;

    // Check exact match first
    if (formData[fieldName] !== undefined) {
      return fieldName;
    }

    // Try case-insensitive match
    const fieldLower = fieldName.toLowerCase();
    const matchingKey = Object.keys(formData).find(
      (key) =>
        key && typeof key === "string" && key.toLowerCase() === fieldLower
    );

    return matchingKey || fieldName;
  };

  // Process INVENTORY_FIELD_SEQUENCE and prioritize mapped fields
  const processedGroups = INVENTORY_FIELD_SEQUENCE.map((group) => {
    // Process fields in the group
    const processedFields = group.fields
      .map((fieldConfig) => {
        const fieldName = fieldConfig?.name;
        if (!fieldName || typeof fieldName !== "string") {
          return null;
        }

        // Find actual field name in formData
        const actualFieldName = findActualFieldName(fieldName);

        // Check if field exists in formData or should always be rendered
        const fieldExists =
          formData[actualFieldName] !== undefined ||
          fieldName === "provider" ||
          fieldName === "category" ||
          fieldName === "notes" ||
          fieldName === "follow_up_date";

        if (!fieldExists) {
          return null;
        }

        // Determine if field is mapped
        const isMapped =
          isFieldMapped(actualFieldName) || isFieldMapped(fieldName);

        return {
          ...fieldConfig,
          name: actualFieldName,
          isMapped,
          priority: isMapped ? 1 : 2, // Mapped fields have priority 1, unmapped have 2
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => {
        // Sort by priority first (mapped fields first), then by original order
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return 0; // Maintain original order within same priority
      });

    return {
      ...group,
      fields: processedFields,
    };
  });

  return processedGroups;
};

/**
 * Gets filtered and ordered fields for provider/category based on mapping API data.
 * Shows mapped fields first (in INVENTORY_FIELD_SEQUENCE order), then unmapped default fields.
 * Always includes essential fields: provider, category, notes, follow_up_date.
 * @param {Set<string>} mappedFields - Set of inventory_column_name values from mapping API (lowercase)
 * @param {Array} INVENTORY_FIELD_SEQUENCE - Default field sequence configuration
 * @param {Object} formData - Current form data to check field existence
 * @param {boolean} hasAddressId - Whether address_id exists (to show address fields)
 * @returns {Array} Array of field group configurations with filtered and ordered fields
 */
export const getFieldsForProviderCategory = (
  mappedFields,
  INVENTORY_FIELD_SEQUENCE,
  formData = {},
  hasAddressId = false
) => {
  // Essential fields that should always be shown
  const essentialFields = new Set([
    "provider",
    "category",
    "notes",
    "follow_up_date",
  ]);

  // Address fields that should be shown if address_id exists
  const addressFields = new Set([
    "std_street_address",
    "std_city",
    "std_state",
    "std_zip",
    "std_country",
  ]);

  // Helper function to check if a field is mapped (case-insensitive)
  const isFieldMapped = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") return false;
    const fieldLower = fieldName.toLowerCase();
    return mappedFields.has(fieldLower);
  };

  // Helper function to check if field should always be shown
  const isEssentialField = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") return false;
    const fieldLower = fieldName.toLowerCase();
    return essentialFields.has(fieldLower);
  };

  // Helper function to check if field is an address field
  const isAddressField = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") return false;
    const fieldLower = fieldName.toLowerCase();
    return addressFields.has(fieldLower);
  };

  // Fields to hide for Analog and Mobility categories
  const fieldsToHideForAnalogMobility = new Set([
    "provider_circuit_id",
    "contract_term",
    "bandwidth",
    "last_mile_provider",
    "end_of_term",
  ]);

  // Helper function to check if field should be rendered
  const shouldRenderField = (fieldName, fieldConfig) => {
    if (!fieldName || typeof fieldName !== "string") return false;

    // Always render essential fields
    if (isEssentialField(fieldName)) return true;

    // Get category from formData (case-sensitive check)
    const category = formData.category || "";
    const isAnalogOrMobility = category === "Analog" || category === "Mobility";

    // Hide specific fields for Analog and Mobility categories
    if (isAnalogOrMobility) {
      const fieldLower = fieldName.toLowerCase();
      if (fieldsToHideForAnalogMobility.has(fieldLower)) {
        return false;
      }
    }

    // Always render address fields if address_id exists
    if (hasAddressId && isAddressField(fieldName)) return true;

    // If no mapping data (empty Set), show all fields from INVENTORY_FIELD_SEQUENCE (fallback)
    if (!mappedFields || mappedFields.size === 0) {
      return true; // Show all fields when no mapping is available
    }

    // Check if field exists in formData
    const fieldExists = formData[fieldName] !== undefined;

    // Render if field is mapped OR if it exists in formData
    return isFieldMapped(fieldName) || fieldExists;
  };

  // Process INVENTORY_FIELD_SEQUENCE groups
  const processedGroups = INVENTORY_FIELD_SEQUENCE.map((group) => {
    // Separate fields into mapped and unmapped
    const mappedFieldsList = [];
    const unmappedFieldsList = [];

    group.fields.forEach((fieldConfig) => {
      const fieldName = fieldConfig?.name;
      if (!fieldName || typeof fieldName !== "string") {
        return;
      }

      // Check if field should be rendered
      if (!shouldRenderField(fieldName, fieldConfig)) {
        return;
      }

      // Determine if field is mapped
      const isMapped = isFieldMapped(fieldName);

      const processedField = {
        ...fieldConfig,
        name: fieldName,
        isMapped,
      };

      // Separate into mapped and unmapped lists
      if (isMapped || isEssentialField(fieldName)) {
        mappedFieldsList.push(processedField);
      } else {
        unmappedFieldsList.push(processedField);
      }
    });

    // Combine: mapped fields first, then unmapped fields
    const orderedFields = [...mappedFieldsList, ...unmappedFieldsList];

    // Only return group if it has fields to render
    if (orderedFields.length === 0) {
      return null;
    }

    return {
      ...group,
      fields: orderedFields,
    };
  }).filter(Boolean); // Remove null groups

  return processedGroups;
};
